import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useHomepageContent } from '@/hooks/useHomepageContent';
import { usePlatformStats } from '@/hooks/usePlatformStats';

function formatNumber(value: number): string {
  return value.toLocaleString('fr-FR');
}

export default function Stats() {
  const { t } = useTranslation();
  const { content } = useHomepageContent();
  const { stats, loading, error, retry } = usePlatformStats();
  const { ref: sectionRef, isVisible } = useScrollReveal(0.1);

  const items = [
    { value: formatNumber(stats.visits), suffix: '', label: t('home.stats.visits', { defaultValue: 'Visites générées' }) },
    { value: formatNumber(stats.products), suffix: '', label: t('home.stats.products', { defaultValue: 'Produits ajoutés' }) },
    { value: formatNumber(stats.services), suffix: '', label: t('home.stats.services', { defaultValue: 'Services proposés' }) },
    { value: formatNumber(stats.sites), suffix: '', label: t('home.stats.sites', { defaultValue: 'Sites créés' }) },
  ];

  const sectionTitle = t('home.stats.title', { defaultValue: content.stats.title || 'La plateforme en chiffres' });
  const sectionSubtitle = t('home.stats.subtitle', { defaultValue: content.stats.subtitle || 'Des chiffres réels, calculés en direct depuis la plateforme Zifek.' });

  return (
    <section
      ref={sectionRef as React.LegacyRef<HTMLElement>}
      className="relative py-12 md:py-16 bg-background-50 border-y border-background-200/50"
    >
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-10">
          <span className="inline-block px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
            {t('home.stats.badge', { defaultValue: 'Chiffres clés' })}
          </span>
          <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-2">
            {sectionTitle}
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
            {sectionSubtitle}
          </p>
        </div>

        {error ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-foreground-500">{error}</p>
            <button
              onClick={retry}
              className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Réessayer
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {items.map((stat, i) => (
              <div
                key={i}
                className={`text-center transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="text-2xl md:text-4xl font-bold font-heading text-primary-500 mb-1">
                  {loading ? (
                    <span className="inline-block w-16 h-8 rounded bg-background-200/60 animate-pulse align-middle" />
                  ) : (
                    <>
                      {stat.value}
                      <span className="text-xl md:text-2xl">{stat.suffix}</span>
                    </>
                  )}
                </div>
                <p className="text-foreground-500 text-xs md:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}